import React, { useState, useEffect, useContext } from "react";
import Select, { components } from "react-select";
import { AuthContext } from "../../../context/authcontext";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import { ApprovalPolicyService, TenantUser } from "../websiteSettings/approvalPolicy.service";
import "../websiteSettings/ApprovalPolicy.css";
import {
  alertPolicyService,
  ProactiveSaleAlertRule,
  TriggerType,
  EventType,
  FrequencyConstant,
  TimingConstant,
  WeekDay,
  EntityField,
  ConditionOperator,
  LifecycleSource
} from "./alertPolicy.service";
import { TimezoneSelect } from "../../../components/timezone";
import { languageOptions } from "../../../components/language";
import { CurrencySelect } from "../../../components/currency";
import { showConfirmationAlert, showSuccessAlert, showErrorAlert } from "../../uiInterface/base-ui/alertservice";

const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
          style={{ marginRight: 8, cursor: "pointer" }}
        />
        <label style={{ cursor: "pointer", margin: 0 }}>{props.label}</label>
      </div>
    </components.Option>
  );
};

interface AlertPolicyBuilderProps {
  entityName: string;
}

const AlertPolicyBuilder: React.FC<AlertPolicyBuilderProps> = ({ entityName }) => {
  const { selectedTenantId } = useContext(AuthContext);
  const userRole = useTenantRole(selectedTenantId || 0);
  const isSalesUser = userRole?.role_key === "SALESUSER";
  const [view, setView] = useState<"list" | "builder">("list");
  const [selectedNode, setSelectedNode] = useState<"title" | "rules" | "ai" | "recipients" | null>(null);

  const [alertRules, setAlertRules] = useState<ProactiveSaleAlertRule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [crmEntityId, setCrmEntityId] = useState<number | null>(null);
  const [triggerTypes, setTriggerTypes] = useState<TriggerType[]>([]);
  const [eventsList, setEventsList] = useState<EventType[]>([]);
  const [frequencies, setFrequencies] = useState<FrequencyConstant[]>([]);
  const [timings, setTimings] = useState<TimingConstant[]>([]);
  const [weekDays, setWeekDays] = useState<WeekDay[]>([]);
  const [entityFields, setEntityFields] = useState<EntityField[]>([]);
  const [conditionOperators, setConditionOperators] = useState<ConditionOperator[]>([]);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [togglingRuleId, setTogglingRuleId] = useState<number | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [lifecycleSources, setLifecycleSources] = useState<LifecycleSource[]>([]);

  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (view === "list") {
      fetchAlertRules();
    } else if (view === "builder") {
      fetchBuilderData();
    }
  }, [view, entityName]);

  const fetchBuilderData = async () => {
    try {
      const formattedEntityKey = entityName.toUpperCase().replace(/S$/, '');
      const [crmRes, triggerRes, freqRes, timeRes, weekDayRes, opRes, recipientTypeRes, channelsRes] = await Promise.all([
        alertPolicyService.getCrmEntities(formattedEntityKey),
        alertPolicyService.getTriggerTypes(),
        alertPolicyService.getFrequencies(),
        alertPolicyService.getTimings(),
        alertPolicyService.getWeekDays(),
        alertPolicyService.getConditionOperators(),
        alertPolicyService.getNotificationRecipientTypes(),
        alertPolicyService.getNotificationChannels()
      ]);

      let fetchedCrmId = null;
      if (crmRes.results && crmRes.results.length > 0) {
        fetchedCrmId = crmRes.results[0].crm_entity_id;
        setCrmEntityId(fetchedCrmId);
      }

      if (fetchedCrmId) {
        const [contextsRes, tasksRes] = await Promise.all([
          alertPolicyService.getProactiveSaleAlertAiContexts(fetchedCrmId),
          alertPolicyService.getProactiveSaleAlertAiTasks(fetchedCrmId)
        ]);
        setAiContextsList(contextsRes.results || []);
        setAiTasksList(tasksRes.results || []);
      }

      setTriggerTypes(triggerRes.results || []);
      setFrequencies(freqRes || []);
      setTimings(timeRes || []);
      setWeekDays(weekDayRes || []);
      setConditionOperators(opRes?.results || (Array.isArray(opRes) ? opRes : []));
      setRecipientTypes(recipientTypeRes?.results || (Array.isArray(recipientTypeRes) ? recipientTypeRes : []));
      setNotificationChannelsList(channelsRes?.results || (Array.isArray(channelsRes) ? channelsRes : []));

      const tenantId = localStorage.getItem("selectedTenantId");
      if (tenantId) {
        ApprovalPolicyService.fetchTenantUsers(tenantId)
          .then(setTenantUsers)
          .catch(err => console.error("Failed to fetch tenant users", err));
          
        alertPolicyService.getLifecycleSources(tenantId)
          .then(res => setLifecycleSources(res.results || []))
          .catch(err => console.error("Failed to fetch lifecycle sources", err));
      }
    } catch (error) {
      console.error("Error fetching builder data:", error);
    }
  };

  useEffect(() => {
    if (view === "builder" && entityName) {
      const fetchEntityFields = async () => {
        try {
          const formattedEntityKey = entityName.toUpperCase().replace(/S$/, '').toLowerCase();
          const response = await alertPolicyService.getEntityFields(formattedEntityKey);
          setEntityFields(response.fields || []);
        } catch (error) {
          console.error("Error fetching entity fields:", error);
        }
      };
      fetchEntityFields();
    }
  }, [view, entityName]);

  useEffect(() => {
    if (crmEntityId && view === "builder") {
      fetchEvents(crmEntityId);
    }
  }, [crmEntityId, view]);

  const fetchEvents = async (id: number) => {
    try {
      const response = await alertPolicyService.getEvents(id);
      setEventsList(response.results || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const fetchAlertRules = async () => {
    setIsLoading(true);
    try {
      const formattedEntityKey = entityName.toUpperCase().replace(/S$/, '');
      const tenantId = localStorage.getItem("selectedTenantId") || undefined;
      const response = await alertPolicyService.getProactiveSaleAlertRules(formattedEntityKey, tenantId);
      setAlertRules(response.results || []);
    } catch (error) {
      console.error("Error fetching alert rules:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [triggerType, setTriggerType] = useState("");
  const [triggerEvent, setTriggerEvent] = useState("");
  const [timezone, setTimezone] = useState("");
  const [frequency, setFrequency] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleFromTime, setScheduleFromTime] = useState("");
  const [scheduleToTime, setScheduleToTime] = useState("");
  const [scheduleDayStr, setScheduleDayStr] = useState("monday");
  const [scheduleDayNum, setScheduleDayNum] = useState<number | "">("");

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [rules, setRules] = useState<{ id?: number; groupKey?: string; field: string; operator: string; value: string; toValue?: string; condition: string; isDirty?: boolean; }[]>([{ field: "", operator: "", value: "", toValue: "", condition: "AND" }]);
  const [involveAi, setInvolveAi] = useState(false);
  const [aiContextsList, setAiContextsList] = useState<any[]>([]);
  const [aiTasksList, setAiTasksList] = useState<any[]>([]);
  const [selectedAiContextIds, setSelectedAiContextIds] = useState<number[]>([]);
  const [selectedAiTaskId, setSelectedAiTaskId] = useState<number | "">("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [isCustomAi, setIsCustomAi] = useState(false);
  const [aiConfigExists, setAiConfigExists] = useState(false);
  const [aiConfigId, setAiConfigId] = useState<number | null>(null);
  const [showAiInfoModal, setShowAiInfoModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSectionLoading, setIsSectionLoading] = useState({ conditions: false, ai: false, recipients: false });
  const [recipientTypes, setRecipientTypes] = useState<any[]>([]);
  const [notificationChannelsList, setNotificationChannelsList] = useState<any[]>([]);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);

  const [recipientsList, setRecipientsList] = useState<{ id?: number; typeId: number | ""; identities: (string | number)[]; emails: string[]; }[]>([{ typeId: "", identities: [], emails: [] }]);
  const [selectedNotificationChannelIds, setSelectedNotificationChannelIds] = useState<number[]>([]);
  const [channelConfigs, setChannelConfigs] = useState<{ id: number; channelId: number }[]>([]);

  const openCreate = (shouldNavigate: boolean = true) => {
    setIsEditMode(false);
    setEditingRuleId(null);
    setTitle("");
    setDescription("");
    setIsActive(true);
    setTriggerType("");
    setTriggerEvent("");
    setTimezone("");
    setFrequency("");
    setScheduleTime("");
    setScheduleFromTime("");
    setScheduleToTime("");
    setScheduleDayStr("monday");
    setScheduleDayNum("");
    setErrors({});
    setInvolveAi(false);
    setSelectedAiContextIds([]);
    setSelectedAiTaskId("");
    setAiPrompt("");
    setIsCustomAi(false);
    setAiConfigExists(false);
    setAiConfigId(null);
    setHasUnsavedChanges(false);
    setRecipientsList([{ typeId: "", identities: [], emails: [] }]);
    setSelectedNotificationChannelIds([]);
    setChannelConfigs([]);
    if (shouldNavigate) {
      setView("builder");
      setSelectedNode("title");
    }
  };

  const handleNodeChange = async (newNode: "title" | "rules" | "ai" | "recipients" | null) => {
    if (hasUnsavedChanges && newNode !== selectedNode) {
      const result = await showConfirmationAlert(
        "Unsaved Changes",
        "You have unsaved changes in the current section. Do you want to save them before moving?",
        undefined,
        {
          showDenyButton: true,
          showCancelButton: false,
          confirmButtonText: "Save Changes",
          denyButtonText: "Discard Changes",
          confirmButtonColor: "#0d6efd",
          denyButtonColor: "#dc3545ff",
          icon: "warning"
        }
      );

      if (result.isConfirmed) {
        const success = await handleSaveRules();
        if (success) {
          setSelectedNode(newNode);
        }
      } else if (result.isDenied) {
        if (editingRuleId) {
          const ruleObj = alertRules.find(r => r.proactive_sale_alert_rule_id === editingRuleId);
          if (ruleObj) {
            await handleEditRule(ruleObj, false);
          }
        } else {
          openCreate(false);
        }
        setHasUnsavedChanges(false);
        setSelectedNode(newNode);
      }
    } else {
      setSelectedNode(newNode);
    }
  };

  const handleBackToList = () => {
    setView("list");
  };

  const addRule = () => {
    setRules([...rules, { field: "", operator: "", value: "", condition: "AND" }]);
    setHasUnsavedChanges(true);
  };

  const updateRule = (index: number, key: string, value: string) => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index] as any, [key]: value, isDirty: true };
    setRules(newRules);
    setHasUnsavedChanges(true);
  };

  const handleConditionSelect = (condition: string) => {
    const newRules = rules.map(rule => ({
      ...rule,
      condition: condition,
      isDirty: true
    }));
    setRules(newRules);
    setHasUnsavedChanges(true);
  };

  const removeRule = async (index: number) => {
    const ruleToDelete = rules[index];
    const result = await showConfirmationAlert(
      "Delete Condition",
      "Are you sure you want to delete this condition? This action cannot be undone."
    );

    if (result.isConfirmed) {
      if (ruleToDelete.id) {
        try {
          await alertPolicyService.deleteProactiveSaleAlertRuleCondition(ruleToDelete.id);
          showSuccessAlert("Condition deleted successfully");
        } catch (error) {
          console.error("Error deleting condition:", error);
          showErrorAlert("Failed to delete condition");
          return;
        }
      }
      const newRules = [...rules];
      newRules.splice(index, 1);
      setRules(newRules);
      setHasUnsavedChanges(true);
    }
  };

  const handleEditRule = async (ruleObj: ProactiveSaleAlertRule, shouldNavigate: boolean = true) => {
    setIsLoading(true);
    try {
      const rule = await alertPolicyService.getProactiveSaleAlertRule(ruleObj.proactive_sale_alert_rule_id);

      setIsEditMode(true);
      setEditingRuleId(rule.proactive_sale_alert_rule_id);
      setTitle(rule.rule_name || "");
      setDescription(rule.rule_description || "");
      setIsActive(rule.is_active);
      setTriggerType(rule.trigger_type?.trigger_type_id ? String(rule.trigger_type.trigger_type_id) : "");
      setTriggerEvent(rule.event?.event_id ? String(rule.event.event_id) : "");
      setTimezone(rule.timezone || "");
      setInvolveAi(rule.is_ai_enabled || false);

      if (rule.schedule_expression) {
        try {
          const schedule = typeof rule.schedule_expression === 'string' ? JSON.parse(rule.schedule_expression) : rule.schedule_expression;
          setFrequency(schedule.frequency || "");
          setScheduleTime(schedule.time || "");
          setScheduleFromTime(schedule.from_time || "");
          setScheduleToTime(schedule.to_time || "");
          if (schedule.frequency === "weekly") {
            setScheduleDayStr(schedule.day || "monday");
          } else if (schedule.frequency === "monthly") {
            setScheduleDayNum(schedule.day || "");
          }
        } catch (e) {
          console.error("Error parsing schedule_expression", e);
        }
      } else {
        setFrequency("");
        setScheduleTime("");
        setScheduleFromTime("");
        setScheduleToTime("");
        setScheduleDayStr("monday");
        setScheduleDayNum("");
      }

      setErrors({});
      if (shouldNavigate) {
        setView("builder");
        setSelectedNode("title");
        setActiveDropdown(null);
      }

      setIsSectionLoading({ conditions: true, ai: true, recipients: true });

      // Background fetch for AI config
      alertPolicyService.getProactiveSaleAlertRuleAiConfiguration(rule.proactive_sale_alert_rule_id)
        .then((res: any) => {
          const aiConfigs = res.results || res;
          if (Array.isArray(aiConfigs) && aiConfigs.length > 0) {
            const config = aiConfigs[0];
            setAiConfigExists(true);
            setAiConfigId(config.proactive_sale_alert_rule_ai_configuration_id || config.id || null);
            setSelectedAiContextIds(config.selected_ai_context_ids || []);
            setSelectedAiTaskId(config.selected_ai_task_ids && config.selected_ai_task_ids.length > 0 ? config.selected_ai_task_ids[0] : "");
            setAiPrompt(config.custom_instruction || "");
            setIsCustomAi(!!config.custom_instruction);
          } else {
            setAiConfigExists(false);
            setAiConfigId(null);
          }
        })
        .catch(err => console.error("Error fetching AI config", err))
        .finally(() => setIsSectionLoading(prev => ({ ...prev, ai: false })));

      // Background fetch for conditions
      alertPolicyService.getProactiveSaleAlertRuleConditions(rule.proactive_sale_alert_rule_id)
        .then((res: any) => {
          const conditionsData = res.results || res;
          if (Array.isArray(conditionsData) && conditionsData.length > 0) {
            const newRules = conditionsData.map((c: any) => {
              const op = c.condition_operator?.condition_operator_id ? String(c.condition_operator.condition_operator_id) : (c.condition_operator_id ? String(c.condition_operator_id) : "");

              const valJson = c.entity_field_value || {};
              let v = valJson.value !== undefined ? String(valJson.value) : "";
              let toV = "";
              if (valJson.from !== undefined && valJson.to !== undefined) {
                v = String(valJson.from);
                toV = String(valJson.to);
              }
              return {
                id: c.proactive_sale_alert_rule_condition_id,
                field: c.entity_field || "",
                operator: op,
                value: v,
                toValue: toV,
                condition: c.condition_join_key || "AND",
                groupKey: c.condition_group_key || undefined,
              };
            });
            setRules(newRules);
          } else {
            setRules([{ field: "", operator: "", value: "", condition: "AND" }]);
          }
        })
        .catch(err => console.error("Error fetching conditions:", err))
        .finally(() => setIsSectionLoading(prev => ({ ...prev, conditions: false })));

      // Background fetch for Channels and Recipients
      Promise.all([
        alertPolicyService.getProactiveSaleAlertRuleNotificationChannels(rule.proactive_sale_alert_rule_id).catch(() => ({ results: [] })),
        alertPolicyService.getProactiveSaleAlertRuleNotificationRecipients(rule.proactive_sale_alert_rule_id).catch(() => ({ results: [] }))
      ]).then(([channelsRes, recipientsRes]) => {
        const channelsData = channelsRes?.results || channelsRes;
        const recipientsData = recipientsRes?.results || recipientsRes;

        if (Array.isArray(channelsData) && channelsData.length > 0) {
          const loadedConfigs = channelsData.map((chan: any) => ({
            id: chan.proactive_sale_alert_rule_notification_channel_id || chan.id,
            channelId: chan.notification_channel?.notification_channel_id || chan.notification_channel_id
          }));
          setChannelConfigs(loadedConfigs);
          setSelectedNotificationChannelIds(loadedConfigs.map((c: any) => c.channelId));
        } else {
          setChannelConfigs([]);
          setSelectedNotificationChannelIds([]);
        }

        if (Array.isArray(recipientsData) && recipientsData.length > 0) {
          const loadedRecipients = recipientsData.map((rec: any) => {
            const id = rec.proactive_sale_alert_rule_notification_recipient_id || rec.id;
            const typeId = rec.notification_recipient_type?.notification_recipient_type_id || rec.notification_recipient_type_id || rec.notification_receipent_type?.notification_receipent_type_id || rec.notification_receipent_type_id;
            let val = rec.recipient_value || rec.receipent_value;
            let identities: (string | number)[] = [];
            let emails: string[] = [];

            if (val) {
              if (typeof val === 'string') {
                try { val = JSON.parse(val); } catch (e) { }
              }
              if (val.user_ids) {
                identities = val.user_ids;
              } else if (val.emails) {
                emails = val.emails || [];
              }
            }
            return { id, typeId: typeId || "", identities, emails };
          });
          setRecipientsList(loadedRecipients);
        } else {
          setRecipientsList([{ typeId: "", identities: [], emails: [] }]);
        }
      }).finally(() => {
        setIsSectionLoading(prev => ({ ...prev, recipients: false }));
      });

    } catch (error) {
      console.error("Error fetching rule details:", error);
      showErrorAlert("Failed to load rule details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (rule: ProactiveSaleAlertRule) => {
    setTogglingRuleId(rule.proactive_sale_alert_rule_id);
    try {
      const payload = { is_active: !rule.is_active };
      await alertPolicyService.updateProactiveSaleAlertRule(rule.proactive_sale_alert_rule_id, payload);
      await fetchAlertRules();
    } catch (error) {
      console.error("Error updating rule status:", error);
    } finally {
      setTogglingRuleId(null);
      setActiveDropdown(null);
    }
  };

  const handleDeleteRule = async (rule: ProactiveSaleAlertRule) => {
    setActiveDropdown(null);
    const result = await showConfirmationAlert(
      "Delete Alert Rule",
      `Are you sure you want to delete "${rule.rule_name}"? This action cannot be undone.`,
      async () => {
        await alertPolicyService.deleteProactiveSaleAlertRule(rule.proactive_sale_alert_rule_id);
      }
    );

    if (result.isConfirmed) {
      showSuccessAlert("Alert rule deleted successfully!");
      fetchAlertRules();
    }
  };

  const handleSaveRules = async (): Promise<boolean> => {
    setIsSaving(true);
    try {
      const tenantId = Number(localStorage.getItem("selectedTenantId"));
      const authResponse = JSON.parse(localStorage.getItem("authResponse") || "{}");
      const userId = authResponse?.user?.user_id || authResponse?.user_id || null;

      if (selectedNode === "title") {
        const selectedTriggerType = triggerTypes.find(t => String(t.trigger_type_id) === String(triggerType));
        const isSchedule = selectedTriggerType?.trigger_type_key === 'SCHEDULE';
        const isEvent = selectedTriggerType?.trigger_type_key === 'EVENT';

        const newErrors: { [key: string]: string } = {};

        if (!title?.trim()) {
          newErrors.title = "Rule Name is required.";
        }

        if (!triggerType) {
          newErrors.triggerType = "Trigger Type is required.";
        }

        if (isEvent && !triggerEvent) {
          newErrors.triggerEvent = "Trigger Event is required.";
        }

        if (isSchedule) {
          if (!frequency) {
            newErrors.frequency = "Frequency is required.";
          } else {
            if (frequency === "hourly") {
              if (!scheduleFromTime) newErrors.scheduleFromTime = "From Time is required.";
              if (!scheduleToTime) newErrors.scheduleToTime = "To Time is required.";
              if (scheduleFromTime && scheduleToTime && scheduleFromTime >= scheduleToTime) {
                newErrors.scheduleToTime = "To Time must be greater than From Time.";
              }
            } else if (frequency === "daily") {
              if (!scheduleTime) newErrors.scheduleTime = "Time is required.";
            } else if (frequency === "weekly") {
              if (!scheduleDayStr) newErrors.scheduleDayStr = "Day is required.";
              if (!scheduleTime) newErrors.scheduleTime = "Time is required.";
            } else if (frequency === "monthly") {
              if (scheduleDayNum === "") {
                newErrors.scheduleDayNum = "Day is required.";
              } else if (Number(scheduleDayNum) > 28) {
                newErrors.scheduleDayNum = "Day value can't be greater than 28.";
              } else if (Number(scheduleDayNum) < 1) {
                newErrors.scheduleDayNum = "Day value can't be less than 1.";
              }
              if (!scheduleTime) newErrors.scheduleTime = "Time is required.";
            }
          }
          if (!timezone) {
            newErrors.timezone = "Timezone is required.";
          }
        }

        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          setIsSaving(false);
          return false;
        }

        setErrors({});

        let schedule_expression = null;
        if (isSchedule && frequency) {
          if (frequency === "hourly") {
            schedule_expression = { frequency: "hourly", from_time: scheduleFromTime, to_time: scheduleToTime };
          } else if (frequency === "daily") {
            schedule_expression = { frequency: "daily", time: scheduleTime };
          } else if (frequency === "weekly") {
            schedule_expression = { frequency: "weekly", day: scheduleDayStr, time: scheduleTime };
          } else if (frequency === "monthly") {
            schedule_expression = { frequency: "monthly", day: Number(scheduleDayNum), time: scheduleTime };
          }
        }

        const payload: any = {
          crm_entity_id: crmEntityId,
          tenant_id: tenantId,
          rule_name: title,
          rule_description: description,
          is_active: isActive,
          trigger_type_id: triggerType ? Number(triggerType) : null,
          schedule_expression: isSchedule ? schedule_expression : null,
          timezone: isSchedule ? timezone : null,
          created_by: userId,
          updated_by: userId,
        };

        if (!isSchedule) {
          payload.event_id = triggerEvent ? Number(triggerEvent) : null;
        }

        if (editingRuleId) {
          await alertPolicyService.updateProactiveSaleAlertRule(editingRuleId, payload);
          showSuccessAlert("Rule details saved successfully!");
        } else {
          const response: any = await alertPolicyService.createProactiveSaleAlertRule(payload);
          const newId = response?.proactive_sale_alert_rule_id || response?.data?.proactive_sale_alert_rule_id || response?.id;
          if (newId) {
            setEditingRuleId(newId);
          }
          showSuccessAlert("Rule details saved successfully!");
        }
        setHasUnsavedChanges(false);
        return true;

      } else if (selectedNode === "rules") {
        if (!editingRuleId) {
          showErrorAlert("Please save the Rule details first before saving conditions.");
          setIsSaving(false);
          return false;
        }

        let existingGroupKey = rules.find(r => r.groupKey)?.groupKey;
        const condition_group_key = existingGroupKey || ("group_" + Math.random().toString(36).substr(2, 9));

        let conditionsSaved = 0;
        let conditionsUpdatedLocally = false;
        const newRules = [...rules];

        for (let i = 0; i < rules.length; i++) {
          const rule = rules[i];
          if (!rule.field || !rule.operator || !rule.value) continue;

          let entity_field_value: any = { value: rule.value };
          const isBetween = conditionOperators.find(op => String(op.condition_operator_id) === String(rule.operator))?.condition_operator_key?.toUpperCase() === 'BETWEEN';
          if (isBetween) {
            entity_field_value = { from: rule.value, to: rule.toValue };
          }

          const conditionPayload = {
            proactive_sale_alert_rule_id: editingRuleId,
            entity_field: rule.field,
            condition_operator_id: Number(rule.operator),
            entity_field_value,
            condition_group_key: rule.id ? rule.groupKey : condition_group_key,
            condition_join_key: rule.condition,
            tenant_id: tenantId,
            created_by: userId,
            updated_by: userId
          };

          if (rule.id) {
            if (rule.isDirty) {
              await alertPolicyService.updateProactiveSaleAlertRuleCondition(rule.id, conditionPayload);
              newRules[i].isDirty = false;
              conditionsUpdatedLocally = true;
            }
            conditionsSaved++;
            continue;
          }

          const res: any = await alertPolicyService.createProactiveSaleAlertRuleCondition(conditionPayload);
          // Assuming the API returns the created object or id, update local state so it isn't POSTed again on next save
          const newId = res?.proactive_sale_alert_rule_condition_id || res?.data?.proactive_sale_alert_rule_condition_id || res?.id;
          if (newId) {
            newRules[i].id = newId;
            newRules[i].groupKey = condition_group_key;
            conditionsUpdatedLocally = true;
          }
          conditionsSaved++;
        }

        if (conditionsUpdatedLocally) {
          setRules(newRules);
        }
        if (conditionsSaved > 0) {
          showSuccessAlert("Conditions saved successfully!");
          setHasUnsavedChanges(false);
          return true;
        } else {
          showErrorAlert("Failed to save conditions.");
          return false;
        }

      } else if (selectedNode === "ai") {
        if (!editingRuleId) {
          showErrorAlert("Please save the Rule details first before saving AI configuration.");
          setIsSaving(false);
          return false;
        }

        if (involveAi && isCustomAi && !aiPrompt.trim()) {
          showErrorAlert("Custom AI Prompt is required.");
          setIsSaving(false);
          return false;
        }

        await alertPolicyService.updateProactiveSaleAlertRule(editingRuleId, {
          is_ai_enabled: involveAi
        });

        if (involveAi) {
          const aiPayload = {
            proactive_sale_alert_rule_id: editingRuleId,
            tenant_id: tenantId, // Using the existing tenantId
            selected_ai_context_ids: selectedAiContextIds,
            selected_ai_task_ids: !isCustomAi && selectedAiTaskId ? [selectedAiTaskId] : [],
            custom_instruction: isCustomAi ? aiPrompt : "",
            created_by: userId,
            updated_by: userId
          };
          if (aiConfigId) {
            await alertPolicyService.updateProactiveSaleAlertRuleAiConfiguration(aiConfigId, aiPayload);
          } else {
            const res: any = await alertPolicyService.createProactiveSaleAlertRuleAiConfiguration(aiPayload);
            const newId = res?.proactive_sale_alert_rule_ai_configuration_id || res?.data?.proactive_sale_alert_rule_ai_configuration_id || res?.id;
            if (newId) {
              setAiConfigId(newId);
            }
            setAiConfigExists(true);
          }
        }

        showSuccessAlert("AI configuration saved successfully!");
        setHasUnsavedChanges(false);
        return true;
      } else if (selectedNode === "recipients") {
        if (!editingRuleId) {
          showErrorAlert("Please save the Rule details first before saving Recipients.");
          setIsSaving(false);
          return false;
        }

        if (selectedNotificationChannelIds.length === 0) {
          showErrorAlert("Please select at least one notification channel.");
          setIsSaving(false);
          return false;
        }

        let recipientsUpdatedLocally = false;
        const newRecipientsList = [...recipientsList];

        for (let i = 0; i < recipientsList.length; i++) {
          const block = recipientsList[i];
          if (!block.typeId) {
            showErrorAlert("Please select a recipient type for all blocks.");
            setIsSaving(false);
            return false;
          }

          const selectedTypeKey = recipientTypes.find(r => r.notification_receipent_type_id == block.typeId)?.notification_receipent_type_key;
          let recipientValue = {};
          if (selectedTypeKey === "TENANT_USER" || selectedTypeKey === "CLIENT_ADMIN") {
            recipientValue = { user_ids: block.identities };
          } else if (selectedTypeKey === "CUSTOM") {
            recipientValue = { emails: block.emails };
          }

          const recipientPayload = {
            proactive_sale_alert_rule_id: editingRuleId,
            notification_recipient_type_id: block.typeId,
            recipient_value: recipientValue,
            tenant_id: tenantId,
            created_by: userId,
            updated_by: userId
          };

          if (block.id) {
            await alertPolicyService.updateProactiveSaleAlertRuleNotificationRecipient(block.id, recipientPayload);
          } else {
            const res: any = await alertPolicyService.createProactiveSaleAlertRuleNotificationRecipient(recipientPayload);
            const newId = res?.proactive_sale_alert_rule_notification_recipient_id || res?.data?.proactive_sale_alert_rule_notification_recipient_id || res?.id;
            if (newId) {
              newRecipientsList[i].id = newId;
              recipientsUpdatedLocally = true;
            }
          }
        }

        if (recipientsUpdatedLocally) {
          setRecipientsList(newRecipientsList);
        }

        const channelsToDelete = channelConfigs.filter(c => !selectedNotificationChannelIds.includes(c.channelId));
        const channelsToCreate = selectedNotificationChannelIds.filter(id => !channelConfigs.some(c => c.channelId === id));

        for (const c of channelsToDelete) {
          await alertPolicyService.deleteProactiveSaleAlertRuleNotificationChannel(c.id);
        }

        const newChannelConfigs = channelConfigs.filter(c => selectedNotificationChannelIds.includes(c.channelId));

        for (const channelId of channelsToCreate) {
          const channelPayload = {
            proactive_sale_alert_rule_id: editingRuleId,
            notification_channel_id: channelId,
            tenant_id: tenantId,
            created_by: userId,
            updated_by: userId
          };
          const res: any = await alertPolicyService.createProactiveSaleAlertRuleNotificationChannel(channelPayload);
          const newId = res?.proactive_sale_alert_rule_notification_channel_id || res?.data?.proactive_sale_alert_rule_notification_channel_id || res?.id;
          if (newId) {
            newChannelConfigs.push({ id: newId, channelId });
          }
        }
        setChannelConfigs(newChannelConfigs);

        showSuccessAlert("Recipients saved successfully!");
        setHasUnsavedChanges(false);
        return true;
      }
      return false;

    } catch (error) {
      console.error("Error creating/updating alert rule:", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const addRecipientBlock = () => {
    setRecipientsList([...recipientsList, { typeId: "", identities: [], emails: [] }]);
    setHasUnsavedChanges(true);
  };

  const removeRecipientBlock = async (index: number) => {
    const blockToDelete = recipientsList[index];
    const result = await showConfirmationAlert(
      "Delete Recipient",
      "Are you sure you want to delete this recipient? This action cannot be undone."
    );

    if (result.isConfirmed) {
      if (blockToDelete.id) {
        try {
          await alertPolicyService.deleteProactiveSaleAlertRuleNotificationRecipient(blockToDelete.id);
          showSuccessAlert("Recipient deleted successfully");
        } catch (error) {
          console.error("Error deleting recipient:", error);
          showErrorAlert("Failed to delete recipient");
          return;
        }
      }
      const newList = [...recipientsList];
      newList.splice(index, 1);
      setRecipientsList(newList);
      setHasUnsavedChanges(true);
    }
  };

  const renderListView = () => (
    <>
      <div className="ap-list-header">
        <h4>{entityName} Alerts</h4>
        {!isSalesUser && (
          <button className="ap-create-btn" onClick={() => openCreate()}>
            <i className="fa fa-plus" />
            Create Alert
          </button>
        )}
      </div>
      {isLoading ? (
        <div className="ap-empty-state">
          <div className="ap-empty-icon">
            <i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} />
          </div>
          <div className="ap-empty-title">Loading...</div>
        </div>
      ) : alertRules.length > 0 ? (
        <div className="ap-policy-grid">
          {alertRules.map((rule) => (
            <div key={rule.proactive_sale_alert_rule_id} className={`ap-policy-card ${rule.is_active ? 'ap-card-active' : 'ap-card-inactive'}`} onClick={() => handleEditRule(rule)} style={{ cursor: "pointer" }}>
              <div className="ap-card-header">
                <div className="ap-card-icon">
                  <i className="ti ti-bell" />
                </div>
                <div style={{ position: "relative" }}>
                  <button className="ap-card-menu-btn" onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === rule.proactive_sale_alert_rule_id ? null : rule.proactive_sale_alert_rule_id); }}>
                    <i className="fa fa-ellipsis-v" />
                  </button>
                  {activeDropdown === rule.proactive_sale_alert_rule_id && (
                    <div className="ap-card-dropdown" onClick={(e) => e.stopPropagation()}>
                      <button className="ap-dropdown-item" onClick={() => handleEditRule(rule)}>
                        <i className="ti ti-eye" /> View Detail
                      </button>
                      {!isSalesUser && (
                        <button className="ap-dropdown-item" onClick={() => handleToggleActive(rule)} disabled={togglingRuleId === rule.proactive_sale_alert_rule_id}>
                          {togglingRuleId === rule.proactive_sale_alert_rule_id ? (
                            <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> {rule.is_active ? 'Deactivating...' : 'Activating...'}</>
                          ) : (
                            <><i className={`ti ${rule.is_active ? 'ti-toggle-left' : 'ti-toggle-right'}`} /> {rule.is_active ? 'Deactivate' : 'Activate'}</>
                          )}
                        </button>
                      )}
                      {!isSalesUser && (
                        <button className="ap-dropdown-item ap-dropdown-danger" onClick={() => handleDeleteRule(rule)}>
                          <i className="ti ti-trash" /> Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="ap-card-title">{rule.rule_name}</div>
              <div className="ap-card-entity">{rule.rule_description}</div>
              <div className="ap-card-badges-bottom">
                <span className={`ap-badge ${rule.is_active ? 'ap-badge-active' : 'ap-badge-inactive'}`}>
                  {rule.is_active ? 'Activate' : 'Deactivate'}
                </span>
                <span className="ap-badge ap-badge-approval">
                  {rule.trigger_type?.trigger_type_display_name || "-"}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="ap-empty-state">
          <div className="ap-empty-icon">
            <i className="ti ti-bell" />
          </div>
          <div className="ap-empty-title">No Alerts</div>
          <p className="ap-empty-desc">
            Create your first alert to automate notifications for {entityName}.
          </p>
          {!isSalesUser && (
            <button className="ap-create-btn" onClick={() => openCreate()}>
              <i className="fa fa-plus" />
              Create Alert
            </button>
          )}
        </div>
      )}
    </>
  );

  const renderFlowCanvas = () => (
    <div className="ap-builder-canvas">
      <div className="ap-flow-container">
        <div
          className={`ap-flow-node ap-flow-node-title ${selectedNode === "title" ? "ap-node-selected" : ""}`}
          onClick={() => handleNodeChange("title")}
        >
          <div className="ap-flow-node-label">
            <i className="fa-solid fa-list-check" />
            {title || "Rules"}
          </div>
        </div>

        <div className="ap-connector">
          <div className="ap-connector-line" />
        </div>

        <div
          className={`ap-flow-node ap-flow-node-rule ${selectedNode === "rules" ? "ap-node-selected" : ""}`}
          onClick={() => handleNodeChange("rules")}
        >
          <div className="ap-flow-node-label">
            <i className="fa fa-filter" />
            Conditions
          </div>
        </div>

        <div className="ap-connector">
          <div className="ap-connector-line" />
        </div>

        <div
          className={`ap-flow-node ap-flow-node-stage ${selectedNode === "ai" ? "ap-node-selected" : ""}`}
          onClick={() => handleNodeChange("ai")}
          style={{ position: 'relative' }}
        >
          <span style={{ position: 'absolute', top: 0, left: 0, background: '#06180038', color: 'white', fontSize: '9px', padding: '2px 6px', borderTopLeftRadius: '10px', borderBottomRightRadius: '4px', fontWeight: 600 }}>Optional</span>
          <div className="ap-flow-node-label">
            <i className="ti ti-brain" />
            AI Configuration
          </div>
        </div>

        <div className="ap-connector">
          <div className="ap-connector-line" />
        </div>

        <div
          className={`ap-flow-node ap-flow-node-recipients ${selectedNode === "recipients" ? "ap-node-selected" : ""}`}
          onClick={() => handleNodeChange("recipients")}
        >
          <div className="ap-flow-node-label">
            <i className="ti ti-users" />
            Recipients
          </div>
        </div>
      </div>
    </div>
  );

  const renderLoader = () => (
    <div className="ap-props-panel" style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ textAlign: "center" }}>
        <i className="ti ti-loader" style={{ fontSize: 24, animation: "spin 1s linear infinite", color: "#64748b" }} />
        <div style={{ marginTop: 8, color: "#64748b", fontSize: 13 }}>Loading this section...</div>
      </div>
    </div>
  );

  const renderPropsPanel = () => {
    if (selectedNode === "rules" && isSectionLoading.conditions) return renderLoader();
    if (selectedNode === "ai" && isSectionLoading.ai) return renderLoader();
    if (selectedNode === "recipients" && isSectionLoading.recipients) return renderLoader();

    return (
      <div className="ap-props-panel">
        {!selectedNode ? (
          <div className="ap-props-empty">
            <div className="ap-props-empty-icon">
              <i className="ti ti-click" />
            </div>
            <p>Select a node on the canvas to view and edit its properties</p>
          </div>
        ) : selectedNode === "title" ? (
          <>
            <div className="ap-props-header">
              <h5>
                <span className="ap-props-header-icon ap-props-icon-title">
                  <i className="fa-solid fa-list-check" />
                </span>
                Rules
              </h5>
            </div>
            <div className="ap-props-body" onChange={() => setHasUnsavedChanges(true)}>
              <fieldset disabled={isSalesUser} style={{ border: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
              <div className="ap-field-group">
                <label className="ap-field-label">Rule Name <span style={{ color: 'orange' }}>*</span></label>
                <input
                  className={`ap-field-input ${errors.title ? "error" : ""}`}
                  type="text"
                  placeholder="Enter rule name"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (errors.title) setErrors({ ...errors, title: "" });
                  }}
                />
                {errors.title && <span className="mx-2" style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.title}</span>}
              </div>
              <div className="ap-field-group">
                <label className="ap-field-label">Description</label>
                <textarea
                  className="ap-field-input"
                  placeholder="Enter description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{ resize: "vertical", minHeight: "50px", padding: "10px" }}
                />
              </div>
              <div className="ap-field-group">
                <label className="ap-field-label">Trigger Type <span style={{ color: 'orange' }}>*</span></label>
                <select
                  className={`ap-field-select ${errors.triggerType ? "error" : ""}`}
                  value={triggerType}
                  onChange={(e) => {
                    setTriggerType(e.target.value);
                    if (errors.triggerType) setErrors({ ...errors, triggerType: "" });
                  }}
                >
                  <option value="">Select Trigger Type</option>
                  {triggerTypes.map(t => (
                    <option key={t.trigger_type_id} value={t.trigger_type_id}>
                      {t.trigger_type_display_name}
                    </option>
                  ))}
                </select>
                {errors.triggerType && <span className="mx-2" style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.triggerType}</span>}
              </div>
              {(() => {
                const selectedTriggerType = triggerTypes.find(t => String(t.trigger_type_id) === String(triggerType));
                const isEvent = selectedTriggerType?.trigger_type_key === 'EVENT';
                const isSchedule = selectedTriggerType?.trigger_type_key === 'SCHEDULE';

                return (
                  <>
                    {isEvent && (
                      <div className="ap-field-group">
                        <label className="ap-field-label">Trigger Event <span style={{ color: 'orange' }}>*</span></label>
                        <select
                          className={`ap-field-select ${errors.triggerEvent ? "error" : ""}`}
                          value={triggerEvent}
                          onChange={(e) => {
                            setTriggerEvent(e.target.value);
                            if (errors.triggerEvent) setErrors({ ...errors, triggerEvent: "" });
                          }}
                        >
                          <option value="">Select Event</option>
                          {eventsList.map(e => (
                            <option key={e.event_id} value={e.event_id}>
                              {e.event_display_name}
                            </option>
                          ))}
                        </select>
                        {errors.triggerEvent && <span className="mx-2" style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.triggerEvent}</span>}
                      </div>
                    )}
                    {isSchedule && (
                      <>
                        <div className="ap-field-group">
                          <label className="ap-field-label">Frequency <span style={{ color: 'orange' }}>*</span></label>
                          <select
                            className={`ap-field-select ${errors.frequency ? "error" : ""}`}
                            value={frequency}
                            onChange={(e) => {
                              setFrequency(e.target.value);
                              if (errors.frequency) setErrors({ ...errors, frequency: "" });
                            }}
                          >
                            <option value="">Select Frequency</option>
                            {frequencies.map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </select>
                          {errors.frequency && <span className="mx-2" style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.frequency}</span>}
                        </div>

                        {frequency === "hourly" && (
                          <div style={{ display: "flex", gap: "10px" }}>
                            <div className="ap-field-group" style={{ flex: 1 }}>
                              <label className="ap-field-label">From Time <span style={{ color: 'orange' }}>*</span></label>
                              <select
                                className={`ap-field-select ${errors.scheduleFromTime ? "error" : ""}`}
                                value={scheduleFromTime}
                                onChange={(e) => {
                                  setScheduleFromTime(e.target.value);
                                  if (errors.scheduleFromTime) setErrors({ ...errors, scheduleFromTime: "" });
                                  if (scheduleToTime && e.target.value >= scheduleToTime) {
                                    setScheduleToTime("");
                                  }
                                }}
                              >
                                <option value="">Select Time</option>
                                {timings.map(t => (
                                  <option key={`from-${t.value}`} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                              {errors.scheduleFromTime && <span className="mx-2" style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.scheduleFromTime}</span>}
                            </div>
                            <div className="ap-field-group" style={{ flex: 1 }}>
                              <label className="ap-field-label">To Time <span style={{ color: 'orange' }}>*</span></label>
                              <select
                                className={`ap-field-select ${errors.scheduleToTime ? "error" : ""}`}
                                value={scheduleToTime}
                                onChange={(e) => {
                                  setScheduleToTime(e.target.value);
                                  if (errors.scheduleToTime) setErrors({ ...errors, scheduleToTime: "" });
                                }}
                              >
                                <option value="">Select Time</option>
                                {timings.map(t => (
                                  <option key={`to-${t.value}`} value={t.value} disabled={scheduleFromTime ? t.value <= scheduleFromTime : false}>{t.label}</option>
                                ))}
                              </select>
                              {errors.scheduleToTime && <span className="mx-2" style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.scheduleToTime}</span>}
                            </div>
                          </div>
                        )}

                        {frequency === "daily" && (
                          <div className="ap-field-group">
                            <label className="ap-field-label">Time <span style={{ color: 'orange' }}>*</span></label>
                            <select
                              className={`ap-field-select ${errors.scheduleTime ? "error" : ""}`}
                              value={scheduleTime}
                              onChange={(e) => {
                                setScheduleTime(e.target.value);
                                if (errors.scheduleTime) setErrors({ ...errors, scheduleTime: "" });
                              }}
                            >
                              <option value="">Select Time</option>
                              {timings.map(t => (
                                <option key={`time-${t.value}`} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                            {errors.scheduleTime && <span className="mx-2" style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.scheduleTime}</span>}
                          </div>
                        )}

                        {frequency === "weekly" && (
                          <div style={{ display: "flex", gap: "10px" }}>
                            <div className="ap-field-group" style={{ flex: 1 }}>
                              <label className="ap-field-label">Day <span style={{ color: 'orange' }}>*</span></label>
                              <select
                                className={`ap-field-select ${errors.scheduleDayStr ? "error" : ""}`}
                                value={scheduleDayStr}
                                onChange={(e) => {
                                  setScheduleDayStr(e.target.value);
                                  if (errors.scheduleDayStr) setErrors({ ...errors, scheduleDayStr: "" });
                                }}
                              >
                                <option value="">Select Day</option>
                                {weekDays.map(d => (
                                  <option key={d.week_day_id} value={d.week_day_key.toLowerCase()}>{d.week_day_display_name}</option>
                                ))}
                              </select>
                              {errors.scheduleDayStr && <span className="mx-2" style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.scheduleDayStr}</span>}
                            </div>
                            <div className="ap-field-group" style={{ flex: 1 }}>
                              <label className="ap-field-label">Time <span style={{ color: 'orange' }}>*</span></label>
                              <select
                                className={`ap-field-select ${errors.scheduleTime ? "error" : ""}`}
                                value={scheduleTime}
                                onChange={(e) => {
                                  setScheduleTime(e.target.value);
                                  if (errors.scheduleTime) setErrors({ ...errors, scheduleTime: "" });
                                }}
                              >
                                <option value="">Select Time</option>
                                {timings.map(t => (
                                  <option key={`time-${t.value}`} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                              {errors.scheduleTime && <span className="mx-2" style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.scheduleTime}</span>}
                            </div>
                          </div>
                        )}

                        {frequency === "monthly" && (
                          <div style={{ display: "flex", gap: "10px" }}>
                            <div className="ap-field-group" style={{ flex: 1 }}>
                              <label className="ap-field-label">Day (1-28) <span style={{ color: 'orange' }}>*</span></label>
                              <input
                                type="number"
                                min="1"
                                max="28"
                                className={`ap-field-input ${errors.scheduleDayNum ? "error" : ""}`}
                                value={scheduleDayNum}
                                onChange={(e) => {
                                  let val = e.target.value;
                                  if (errors.scheduleDayNum) setErrors({ ...errors, scheduleDayNum: "" });
                                  if (val === "") {
                                    setScheduleDayNum("");
                                    return;
                                  }
                                  let num = Number(val);
                                  setScheduleDayNum(num);
                                }}
                              />
                              {errors.scheduleDayNum && <span className="mx-2" style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.scheduleDayNum}</span>}
                            </div>
                            <div className="ap-field-group" style={{ flex: 1 }}>
                              <label className="ap-field-label">Time <span style={{ color: 'orange' }}>*</span></label>
                              <select
                                className={`ap-field-select ${errors.scheduleTime ? "error" : ""}`}
                                value={scheduleTime}
                                onChange={(e) => {
                                  setScheduleTime(e.target.value);
                                  if (errors.scheduleTime) setErrors({ ...errors, scheduleTime: "" });
                                }}
                              >
                                <option value="">Select Time</option>
                                {timings.map(t => (
                                  <option key={`time-${t.value}`} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                              {errors.scheduleTime && <span style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.scheduleTime}</span>}
                            </div>
                          </div>
                        )}

                        <div className="ap-field-group mb-2">
                          <label className="ap-field-label">Timezone <span style={{ color: 'orange' }}>*</span></label>
                          <TimezoneSelect
                            value={timezone}
                            onChange={(val) => {
                              setTimezone(val);
                              if (errors.timezone) setErrors({ ...errors, timezone: "" });
                            }}
                            isDisabled={isSalesUser}
                          />
                          {errors.timezone && <span style={{ color: 'red', fontSize: '12px', display: 'block' }}>{errors.timezone}</span>}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
              <div className="ap-toggle-row">
                <div className="ap-toggle-info">
                  <span className="ap-toggle-title">Active</span>
                  <span className="ap-toggle-desc">Enable or disable this rule</span>
                </div>
                <label className="ap-switch">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <span className="ap-switch-slider" />
                </label>
              </div>
              </fieldset>
            </div>
            {!isSalesUser && (
              <div className="ap-props-footer">
                <button className="ap-btn-cancel" onClick={handleBackToList}>Cancel</button>
                <button className="ap-btn-save" onClick={handleSaveRules} disabled={isSaving}>
                  {isSaving ? (
                    <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite", marginRight: 8 }} />Saving...</>
                  ) : "Save Rules"}
                </button>
              </div>
            )}
          </>
        ) : selectedNode === "rules" ? (
          <>
            <div className="ap-props-header">
              <h5>
                <span className="ap-props-header-icon ap-props-icon-rule">
                  <i className="fa fa-filter" />
                </span>
                Conditions
              </h5>
            </div>
            <div className="ap-props-body" onChange={() => setHasUnsavedChanges(true)}>
              <fieldset disabled={isSalesUser} style={{ border: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
              <div className="ap-panel-list">
                {rules.map((rule, idx) => (
                  <React.Fragment key={idx}>
                    <div className="ap-panel-list-item ap-panel-item-editing" style={{ position: 'relative' }}>
                      <div className="ap-panel-edit-form" style={{ paddingTop: '20px' }}>
                        {!isSalesUser && (
                          <div style={{ position: 'absolute', top: 12, right: 12 }}>
                            <button className="ap-panel-icon-btn ap-panel-icon-danger" onClick={() => removeRule(idx)} title="Remove Condition">
                              <i className="ti ti-trash" />
                            </button>
                          </div>
                        )}
                        <div className="ap-field-group">
                          <label className="ap-field-label">Field</label>
                          <select
                            className="ap-field-select"
                            value={rule.field}
                            onChange={(e) => {
                              const newRules = [...rules];
                              newRules[idx] = {
                                ...newRules[idx],
                                field: e.target.value,
                                operator: "",
                                value: "",
                                toValue: ""
                              };
                              setRules(newRules);
                              setHasUnsavedChanges(true);
                            }}
                          >
                            <option value="">Select Field</option>
                            {entityFields.map((f) => (
                              <option key={f.field_name} value={f.field_name}>{f.display_name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="ap-field-group">
                          <label className="ap-field-label">Operator</label>
                          <select
                            className="ap-field-select"
                            value={rule.operator}
                            onChange={(e) => updateRule(idx, "operator", e.target.value)}
                            disabled={!rule.field}
                          >
                            {!rule.field ? (
                              <option value="">Select Field First</option>
                            ) : (
                              <>
                                <option value="">Select Operator</option>
                                {(() => {
                                  const selectedField = entityFields.find(f => f.field_name === rule.field);
                                  if (!selectedField) return null;
                                  const allowedOps = selectedField.allowed_operators || [];
                                  return conditionOperators
                                    .filter(op => {
                                      return allowedOps.some((aop: any) => {
                                        if (typeof aop === 'object' && aop !== null) {
                                          return aop.condition_operator_id === op.condition_operator_id || aop.id === op.condition_operator_id ||
                                            (typeof aop.condition_operator_key === 'string' && aop.condition_operator_key.toUpperCase() === op.condition_operator_key.toUpperCase()) ||
                                            (typeof aop.key === 'string' && aop.key.toUpperCase() === op.condition_operator_key.toUpperCase());
                                        }
                                        if (typeof aop === 'string') {
                                          return aop.toUpperCase() === op.condition_operator_key.toUpperCase() || aop === String(op.condition_operator_id);
                                        }
                                        return aop === op.condition_operator_id;
                                      });
                                    })
                                    .map(op => (
                                      <option key={op.condition_operator_id} value={String(op.condition_operator_id)}>
                                        {op.condition_operator_display_name}
                                      </option>
                                    ));
                                })()}
                              </>
                            )}
                          </select>
                        </div>
                        <div className="ap-field-group">
                          <label className="ap-field-label">Value</label>
                          {(() => {
                            const selectedField = entityFields.find(f => f.field_name === rule.field);
                            const isBetween = conditionOperators.find(op => String(op.condition_operator_id) === String(rule.operator))?.condition_operator_key?.toUpperCase() === 'BETWEEN';
                            const dt = selectedField?.data_type || 'string';
                            let inputType = 'text';
                            if (dt === 'number' || dt === 'integer' || dt === 'float' || dt === 'decimal') inputType = 'number';
                            if (dt === 'date' || dt === 'datetime') inputType = 'date';

                            if (rule.field === 'currency_code' || rule.field === 'currency' || dt === 'currency') {
                              return (
                                <CurrencySelect
                                  value={rule.value || ""}
                                  onChange={(v: string) => updateRule(idx, "value", v)}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  isDisabled={isSalesUser}
                                  styles={{
                                    menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
                                    menu: (base: any) => ({ ...base, zIndex: 99999 }),
                                    control: (base: any) => ({ ...base, fontSize: 13, minHeight: 38, width: "100%", minWidth: 200 }),
                                  }}
                                />
                              );
                            }

                            if (rule.field.startsWith('is_') || dt === 'boolean') {
                              return (
                                <select
                                  className="ap-field-select"
                                  value={rule.value || ""}
                                  onChange={(e) => updateRule(idx, "value", e.target.value)}
                                >
                                  <option value="">Select option</option>
                                  <option value="true">True</option>
                                  <option value="false">False</option>
                                </select>
                              );
                            }

                            if (rule.field === 'timezone' || rule.field === 'timezone_id' || rule.field === 'time_zone' || dt === 'timezone') {
                              return (
                                <TimezoneSelect
                                  value={rule.value}
                                  onChange={(val: string) => updateRule(idx, "value", val)}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  isDisabled={isSalesUser}
                                  styles={{
                                    menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
                                    menu: (base: any) => ({ ...base, zIndex: 99999 }),
                                    control: (base: any) => ({ ...base, fontSize: 13, minHeight: 38, width: "100%", minWidth: 200 }),
                                  }}
                                />
                              );
                            }

                            if (rule.field === 'language' || rule.field === 'language_code' || rule.field === 'preferred_language' || dt === 'language') {
                              return (
                                <Select
                                  options={languageOptions}
                                  value={languageOptions.find(opt => opt.value === rule.value) || null}
                                  onChange={(opt: any) => updateRule(idx, "value", opt?.value || "")}
                                  placeholder="Select Language"
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  isDisabled={isSalesUser}
                                  styles={{
                                    menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
                                    menu: (base: any) => ({ ...base, zIndex: 99999 }),
                                    control: (base: any) => ({
                                      ...base,
                                      fontSize: 13,
                                      minHeight: 38,
                                      width: "100%",
                                      minWidth: 200
                                    })
                                  }}
                                />
                              );
                            }

                            if (rule.field === 'source') {
                              return (
                                <Select
                                  options={lifecycleSources.map(s => ({ label: s.lifecycle_source_display_name, value: s.lifecycle_source_key }))}
                                  value={lifecycleSources.map(s => ({ label: s.lifecycle_source_display_name, value: s.lifecycle_source_key })).find(opt => opt.value === rule.value) || null}
                                  onChange={(opt: any) => updateRule(idx, "value", opt?.value || "")}
                                  placeholder="Select Source"
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  isDisabled={isSalesUser}
                                  styles={{
                                    menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
                                    menu: (base: any) => ({ ...base, zIndex: 99999 }),
                                    control: (base: any) => ({ ...base, fontSize: 13, minHeight: 38, width: "100%", minWidth: 200 }),
                                  }}
                                />
                              );
                            }


                            if (isBetween) {
                              return (
                                <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                                  <input className="ap-field-input" style={{ width: '50%', minWidth: 0 }} type={inputType} placeholder="From" value={rule.value} onChange={(e) => updateRule(idx, "value", e.target.value)} />
                                  <input className="ap-field-input" style={{ width: '50%', minWidth: 0 }} type={inputType} placeholder="To" value={rule.toValue || ""} onChange={(e) => updateRule(idx, "toValue", e.target.value)} />
                                </div>
                              );
                            }
                            return (
                              <input className="ap-field-input" type={inputType} placeholder="Enter value" value={rule.value} onChange={(e) => updateRule(idx, "value", e.target.value)} />
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    {idx < rules.length - 1 && (
                      <div className="ap-rule-connector mt-3 mb-3 d-flex justify-content-center">
                        <div className="ap-join-picker" style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 20, padding: 4 }}>
                          <button
                            className={`btn btn-sm ${rule.condition === "AND" ? "btn-primary text-white shadow-sm" : "text-muted"}`}
                            onClick={() => handleConditionSelect("AND")}
                            style={{ borderRadius: 16, fontSize: 12, padding: '2px 12px' }}
                          >AND</button>
                          <button
                            className={`btn btn-sm ${rule.condition === "OR" ? "btn-primary text-white shadow-sm" : "text-muted"}`}
                            onClick={() => handleConditionSelect("OR")}
                            style={{ borderRadius: 16, fontSize: 12, padding: '2px 12px' }}
                          >OR</button>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <span className="text-muted text-center" style={{ display: rules.length === 0 ? "block" : "none" }}>No Condition added yet</span>
              {!isSalesUser && (
                <button className="ap-panel-add-btn mt-2" onClick={addRule}>
                  <i className="fa fa-plus" /> Add Condition
                </button>
              )}
              </fieldset>
            </div>
            {!isSalesUser && (
              <div className="ap-props-footer">
                <button className="ap-btn-cancel" onClick={handleBackToList}>Cancel</button>
                <button className="ap-btn-save" onClick={handleSaveRules} disabled={isSaving}>
                  {isSaving ? (
                    <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite", marginRight: 8 }} />Saving...</>
                  ) : "Save Conditions"}
                </button>
              </div>
            )}
          </>
        ) : selectedNode === "ai" ? (
          <>
            <div className="ap-props-header d-flex justify-content-between align-items-center">
              <h5>
                <span className="ap-props-header-icon ap-props-icon-stage" style={{ background: "#8b5cf6", color: "#fff" }}>
                  <i className="ti ti-brain" />
                </span>
                AI Configuration
              </h5>
              <button className="btn btn-sm btn-icon text-muted" onClick={() => setShowAiInfoModal(true)} title="Information" style={{ padding: 0 }}>
                <i className="ti ti-info-circle text-primary" style={{ fontSize: '1.75rem' }} />
              </button>
            </div>
            <div className="ap-props-body" onChange={() => setHasUnsavedChanges(true)}>
              <fieldset disabled={isSalesUser} style={{ border: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
              <div className="ap-toggle-row">
                <div className="ap-toggle-info">
                  <span className="ap-toggle-title">Involve AI</span>
                  <span className="ap-toggle-desc">Use AI to analyze this alert</span>
                </div>
                <label className="ap-switch">
                  <input
                    type="checkbox"
                    checked={involveAi}
                    onChange={(e) => setInvolveAi(e.target.checked)}
                  />
                  <span className="ap-switch-slider" />
                </label>
              </div>

              {involveAi && (
                <div className="ap-panel-edit-form">
                  <div className="ap-field-group">
                    <label className="ap-field-label">Context</label>
                    <Select
                      isMulti
                      options={aiContextsList.map(c => ({ label: c.proactive_sale_alert_ai_context_display_name, value: c.proactive_sale_alert_ai_context_id }))}
                      value={aiContextsList.filter(c => selectedAiContextIds.includes(c.proactive_sale_alert_ai_context_id)).map(c => ({ label: c.proactive_sale_alert_ai_context_display_name, value: c.proactive_sale_alert_ai_context_id }))}
                      onChange={(opts: any) => {
                        setSelectedAiContextIds(opts ? opts.map((o: any) => o.value) : []);
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Select Context(s)"
                      styles={{
                        control: (base: any) => ({ ...base, minHeight: 38, fontSize: 13 }),
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                      }}
                      menuPortalTarget={document.body}
                      isDisabled={isSalesUser}
                    />
                  </div>

                  <div className="ap-toggle-row mt-2 mb-1">
                    <div className="ap-toggle-info">
                      <span className="ap-toggle-title">Custom Prompt</span>
                    </div>
                    <label className="ap-switch">
                      <input
                        type="checkbox"
                        checked={isCustomAi}
                        onChange={(e) => setIsCustomAi(e.target.checked)}
                      />
                      <span className="ap-switch-slider" />
                    </label>
                  </div>

                  {!isCustomAi ? (
                    <div className="ap-field-group">
                      <label className="ap-field-label">Task</label>
                      <Select
                        options={aiTasksList.map(t => ({ label: t.proactive_sale_alert_ai_task_display_name, value: t.proactive_sale_alert_ai_task_id }))}
                        value={aiTasksList.filter(t => t.proactive_sale_alert_ai_task_id === selectedAiTaskId).map(t => ({ label: t.proactive_sale_alert_ai_task_display_name, value: t.proactive_sale_alert_ai_task_id }))[0] || null}
                        onChange={(opt: any) => {
                          setSelectedAiTaskId(opt ? opt.value : "");
                          setHasUnsavedChanges(true);
                        }}
                        placeholder="Select Task"
                        styles={{
                          control: (base: any) => ({ ...base, minHeight: 38, fontSize: 13 }),
                          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                        }}
                        menuPortalTarget={document.body}
                        isDisabled={isSalesUser}
                      />
                    </div>
                  ) : (
                    <div className="ap-field-group">
                      <label className="ap-field-label">Custom AI Prompt <span style={{ color: '#f46a6a' }}>*</span></label>
                      <textarea
                        className="ap-field-input"
                        rows={3}
                        placeholder="E.g., Please analyze the selected attributes and suggest the next best action."
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        style={{ resize: "vertical", minHeight: "80px", padding: "10px" }}
                      />
                    </div>
                  )}
                </div>
              )}
              </fieldset>
            </div>
            {!isSalesUser && (
              <div className="ap-props-footer">
                <button className="ap-btn-cancel" onClick={handleBackToList}>Cancel</button>
                <button className="ap-btn-save" onClick={handleSaveRules} disabled={isSaving}>
                  {isSaving ? (
                    <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite", marginRight: 8 }} />Saving...</>
                  ) : "Save AI Configuration"}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="ap-props-header">
              <h5>
                <span className="ap-props-header-icon ap-props-icon-recipients" style={{ background: "#a7008b", color: "#fff" }}>
                  <i className="ti ti-users" />
                </span>
                Recipients
              </h5>
            </div>
            <div className="ap-props-body" onChange={() => setHasUnsavedChanges(true)}>
              <fieldset disabled={isSalesUser} style={{ border: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
              <div className="mb-3">
                <label className="ap-field-label" style={{ fontSize: '14px', fontWeight: 600 }}>Who should receive this alert?</label>
              </div>
              <div className="ap-panel-list">
                {recipientsList.map((block, idx) => {
                  const selectedTypeKey = recipientTypes.find(r => r.notification_receipent_type_id == block.typeId)?.notification_receipent_type_key;
                  return (
                    <div key={idx} className="ap-panel-list-item ap-panel-item-editing" style={{ position: 'relative', marginBottom: 16 }}>
                      <div className="ap-panel-edit-form" style={{ paddingTop: '20px' }}>
                        {!isSalesUser && (
                          <div style={{ position: 'absolute', top: 12, right: 12 }}>
                            <button className="ap-panel-icon-btn ap-panel-icon-danger" onClick={() => removeRecipientBlock(idx)} title="Remove Recipient">
                              <i className="ti ti-trash" />
                            </button>
                          </div>
                        )}
                        <div className="ap-field-group">
                          <label className="ap-field-label">Recipient Type</label>
                          <select
                            className="ap-field-select"
                            value={block.typeId}
                            onChange={(e) => {
                              const newRecipients = [...recipientsList];
                              newRecipients[idx].typeId = Number(e.target.value) || "";
                              newRecipients[idx].identities = [];
                              newRecipients[idx].emails = [];
                              setRecipientsList(newRecipients);
                              setHasUnsavedChanges(true);
                            }}
                          >
                            <option value="">Select Recipient Type</option>
                            {recipientTypes.map(rt => (
                              <option key={rt.notification_receipent_type_id} value={rt.notification_receipent_type_id}>
                                {rt.notification_receipent_type_display_name}
                              </option>
                            ))}
                          </select>
                          {(() => {
                            const selectedType = recipientTypes.find(r => r.notification_receipent_type_id == block.typeId);
                            if (selectedType && selectedType.description) {
                              return <span style={{ fontSize: 13, color: "#64748b", marginTop: 4, display: "block" }}>{selectedType.description}</span>;
                            }
                            return null;
                          })()}
                        </div>

                        {selectedTypeKey === "TENANT_USER" && (
                          <div className="ap-field-group mt-1">
                            <label className="ap-field-label">Select Tenant Users</label>
                            <Select
                              isMulti
                              hideSelectedOptions={false}
                              closeMenuOnSelect={false}
                              components={{ Option: CustomOption }}
                              placeholder="Select tenant users..."
                              options={tenantUsers.filter(u => u.role_display_name === "Sales User").map(user => ({
                                value: user.user_id,
                                label: (user.user_first_name + " " + user.user_last_name) || user.email || 'Unknown User'
                              }))}
                              value={tenantUsers
                                .filter(u => u.role_display_name === "Sales User" && block.identities.map(String).includes(String(u.user_id)))
                                .map(u => ({
                                  value: u.user_id,
                                  label: (u.user_first_name + " " + u.user_last_name) || u.email || 'Unknown User'
                                }))}
                              onChange={(selectedOptions) => {
                                const newRecipients = [...recipientsList];
                                newRecipients[idx].identities = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
                                setRecipientsList(newRecipients);
                                setHasUnsavedChanges(true);
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              isDisabled={isSalesUser}
                              styles={{
                                menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
                                control: (base: any) => ({ ...base, fontSize: 13, minHeight: 38 }),
                                menu: (base: any) => ({ ...base, fontSize: 13, zIndex: 9999 })
                              }}
                            />
                          </div>
                        )}

                        {selectedTypeKey === "CLIENT_ADMIN" && (
                          <div className="ap-field-group mt-3">
                            <label className="ap-field-label">Select Client Admins</label>
                            <Select
                              isMulti
                              hideSelectedOptions={false}
                              closeMenuOnSelect={false}
                              components={{ Option: CustomOption }}
                              placeholder="Select client admins..."
                              options={tenantUsers.filter(u => u.role_display_name === "Client Admin").map(user => ({
                                value: user.user_id,
                                label: (user.user_first_name + " " + user.user_last_name) || user.email || 'Unknown User'
                              }))}
                              value={tenantUsers
                                .filter(u => u.role_display_name === "Client Admin" && block.identities.map(String).includes(String(u.user_id)))
                                .map(u => ({
                                  value: u.user_id,
                                  label: (u.user_first_name + " " + u.user_last_name) || u.email || 'Unknown User'
                                }))}
                              onChange={(selectedOptions) => {
                                const newRecipients = [...recipientsList];
                                newRecipients[idx].identities = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
                                setRecipientsList(newRecipients);
                                setHasUnsavedChanges(true);
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              isDisabled={isSalesUser}
                              styles={{
                                menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
                                control: (base: any) => ({ ...base, fontSize: 13, minHeight: 38 }),
                                menu: (base: any) => ({ ...base, fontSize: 13, zIndex: 9999 })
                              }}
                            />
                          </div>
                        )}

                        {selectedTypeKey === "CUSTOM" && (
                          <div className="ap-field-group mt-3">
                            <label className="ap-field-label">External Emails</label>
                            <div className="ap-chip-input-container" style={{ display: "flex", flexWrap: "wrap", gap: 8, border: "1px solid #e2e8f0", borderRadius: 4, padding: "4px 8px", minHeight: 38, cursor: "text" }}>
                              {block.emails.map((email, eIdx) => (
                                <span key={eIdx} style={{ background: "#e2e8f0", padding: "2px 8px", borderRadius: 12, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                                  {email}
                                  {!isSalesUser && (
                                    <i className="ti ti-x" style={{ cursor: "pointer" }} onClick={() => {
                                      const newRecipients = [...recipientsList];
                                      newRecipients[idx].emails = newRecipients[idx].emails.filter((_, i) => i !== eIdx);
                                      setRecipientsList(newRecipients);
                                      setHasUnsavedChanges(true);
                                    }} />
                                  )}
                                </span>
                              ))}
                              <input
                                type="text"
                                style={{ border: "none", outline: "none", flex: 1, minWidth: 100, fontSize: 13, display: isSalesUser ? 'none' : 'block' }}
                                placeholder="Add email..."
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === ",") {
                                    e.preventDefault();
                                    const val = e.currentTarget.value.trim();
                                    if (val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                                      if (!block.emails.includes(val)) {
                                        const newRecipients = [...recipientsList];
                                        newRecipients[idx].emails.push(val);
                                        setRecipientsList(newRecipients);
                                        setHasUnsavedChanges(true);
                                      }
                                      e.currentTarget.value = "";
                                    }
                                  }
                                }}
                              />
                            </div>
                            {!isSalesUser && (
                              <span className="text-muted mx-2" style={{ fontSize: '11px' }}>Press Enter or comma to add email</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {recipientsList.length === 0 && (
                <span className="text-muted text-center" style={{ display: "block", marginBottom: 16 }}>No Recipient added yet</span>
              )}

              {!isSalesUser && (
                <button className="ap-panel-add-btn mt-2 mb-4" onClick={addRecipientBlock}>
                  <i className="fa fa-plus" /> Add Recipient
                </button>
              )}

              <div className="ap-field-group mb-4">
                <label className="ap-field-label">Notification Channels</label>
                <Select
                  isMulti
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  components={{ Option: CustomOption }}
                  placeholder="Select Channels..."
                  options={notificationChannelsList.map(ch => ({
                    value: ch.notification_channel_id,
                    label: ch.notification_channel_display_name
                  }))}
                  value={notificationChannelsList
                    .filter(ch => selectedNotificationChannelIds.includes(ch.notification_channel_id))
                    .map(ch => ({
                      value: ch.notification_channel_id,
                      label: ch.notification_channel_display_name
                    }))}
                  onChange={(selectedOptions) => {
                    setSelectedNotificationChannelIds(selectedOptions ? selectedOptions.map((opt: any) => opt.value) : []);
                    setHasUnsavedChanges(true);
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isDisabled={isSalesUser}
                  styles={{
                    menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
                    control: (base: any) => ({ ...base, fontSize: 13, minHeight: 38 }),
                    menu: (base: any) => ({ ...base, fontSize: 13, zIndex: 9999 })
                  }}
                />
              </div>
              </fieldset>
            </div>
            {!isSalesUser && (
              <div className="ap-props-footer">
                <button className="ap-btn-cancel" onClick={handleBackToList}>Cancel</button>
                <button className="ap-btn-save" onClick={handleSaveRules} disabled={isSaving}>
                  {isSaving ? (
                    <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite", marginRight: 8 }} />Saving...</>
                  ) : "Save Recipients"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="ap-wrapper">
      {isSalesUser && (
        <style>{`
          .ap-props-body input:disabled,
          .ap-props-body select:disabled,
          .ap-props-body textarea:disabled,
          .ap-props-body button:disabled,
          .ap-switch input:disabled ~ .ap-switch-slider,
          .ap-props-body [class*="-control"],
          .ap-props-body [class*="-Control"] {
            cursor: default !important;
          }
        `}</style>
      )}
      {view === "list" ? (
        renderListView()
      ) : (
        <>
          <div className="ap-builder-header">
            <div className="ap-builder-title" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="ap-back-btn" onClick={handleBackToList} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20 }}>
                <i className="fa fa-arrow-left" />
              </button>
              <div>
                <h4 className="ap-title-text" style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700 }}>Alert Builder</h4>
                <div className="ap-title-sub" style={{ fontSize: "0.85rem", color: "#64748b" }}>Configure {entityName} alert rules</div>
              </div>
            </div>
          </div>

          <div className="ap-builder-layout">
            {renderFlowCanvas()}
            {renderPropsPanel()}
          </div>
        </>
      )}

      {/* AI Info Modal */}
      {showAiInfoModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-xl" style={{ minWidth: '80%', maxWidth: '1140px' }}>
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header bg-light border-bottom-0 pb-0" style={{ padding: '24px 32px 16px' }}>
                <h4 className="modal-title fw-bold d-flex align-items-center" style={{ color: '#1e293b' }}>
                  <div className="d-flex align-items-center justify-content-center me-3" style={{ width: 44, height: 44, borderRadius: '12px', background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                    <i className="ti ti-brain fs-3" />
                  </div>
                  AI Configuration Guide
                </h4>
                <button type="button" className="btn-close" onClick={() => setShowAiInfoModal(false)} style={{ boxShadow: 'none' }}><i className="fa fa-close text-primary"></i></button>
              </div>
              <div className="modal-body" style={{ padding: '24px 32px' }}>
                <div className="mb-4 p-4 rounded-3" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <div className="d-flex align-items-center mb-2">
                    <i className="ti ti-toggle-right text-primary me-2" />
                    <h6 className="fw-bold mb-0 text-dark">Involve AI</h6>
                  </div>
                  <p className="text-muted mb-0" style={{ fontSize: '15px' }}>
                    Activating this tells the AI to analyze the specific entity (e.g., Lead, Pipeline) based on the alert rules you've set.
                  </p>
                </div>

                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-3">
                      <i className="ti ti-layers-linked text-primary me-2" />
                      <h6 className="fw-bold mb-0 text-dark">Context Options</h6>
                    </div>
                    <p className="text-muted" style={{ fontSize: '14px' }}>Attaches additional background information for the AI to understand the record better:</p>
                    <div className="d-flex flex-column gap-3">
                      <div className="d-flex align-items-start gap-3 p-3 rounded-3" style={{ border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                        <i className="ti ti-message-circle text-primary mt-1" />
                        <div>
                          <span className="fw-bold d-block text-dark" style={{ fontSize: '14px' }}>Communication History</span>
                          <span className="text-muted" style={{ fontSize: '13px' }}>Include emails, messages, and notes.</span>
                        </div>
                      </div>
                      <div className="d-flex align-items-start gap-3 p-3 rounded-3" style={{ border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                        <i className="ti ti-database text-primary mt-1" />
                        <div>
                          <span className="fw-bold d-block text-dark" style={{ fontSize: '14px' }}>Related Records</span>
                          <span className="text-muted" style={{ fontSize: '13px' }}>Include related CRM records for better understanding.</span>
                        </div>
                      </div>
                      <div className="d-flex align-items-start gap-3 p-3 rounded-3" style={{ border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                        <i className="ti ti-forms text-primary mt-1" />
                        <div>
                          <span className="fw-bold d-block text-dark" style={{ fontSize: '14px' }}>Custom Fields</span>
                          <span className="text-muted" style={{ fontSize: '13px' }}>Include custom fields and business-specific information.</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <i className="ti ti-list-check text-primary me-2" />
                      <h6 className="fw-bold mb-0 text-dark">Task Options</h6>
                    </div>
                    <p className="text-muted" style={{ fontSize: '14px' }}>Defines what the AI will actually do with the provided context:</p>
                    <div className="d-flex flex-column gap-3">
                      <div className="d-flex align-items-start gap-3 p-3 rounded-3" style={{ border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                        <i className="ti ti-file-description text-primary mt-1" />
                        <div>
                          <span className="fw-bold d-block text-dark" style={{ fontSize: '14px' }}>Generate Summary</span>
                          <span className="text-muted" style={{ fontSize: '13px' }}>Create a concise summary of the record.</span>
                        </div>
                      </div>
                      <div className="d-flex align-items-start gap-3 p-3 rounded-3" style={{ border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                        <i className="ti ti-bulb text-primary mt-1" />
                        <div>
                          <span className="fw-bold d-block text-dark" style={{ fontSize: '14px' }}>Suggest Next Step</span>
                          <span className="text-muted" style={{ fontSize: '13px' }}>Recommend the next best action.</span>
                        </div>
                      </div>
                      <div className="d-flex align-items-start gap-3 p-3 rounded-3" style={{ border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                        <i className="ti ti-alert-triangle text-warning mt-1" />
                        <div>
                          <span className="fw-bold d-block text-dark" style={{ fontSize: '14px' }}>Identify Risks</span>
                          <span className="text-muted" style={{ fontSize: '13px' }}>Highlight possible risks or issues.</span>
                        </div>
                      </div>
                      <div className="d-flex align-items-start gap-3 p-3 rounded-3" style={{ border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                        <i className="fa fa-search text-success mt-1" />
                        <div>
                          <span className="fw-bold d-block text-dark" style={{ fontSize: '14px' }}>Find Opportunities</span>
                          <span className="text-muted" style={{ fontSize: '13px' }}>Suggest opportunities to improve outcomes.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 rounded-3" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <div className="d-flex align-items-center mb-2">
                    <i className="ti ti-prompt text-primary me-2" />
                    <h6 className="fw-bold mb-0 text-dark">Custom AI Prompt</h6>
                  </div>
                  <p className="text-muted mb-0" style={{ fontSize: '15px' }}>
                    A custom way to give instructions to the AI in your own words, like "make the response concise and professional."
                  </p>
                </div>
              </div>
              <div className="modal-footer border-top-0 d-flex justify-content-end" style={{ padding: '16px 32px 24px' }}>
                <button type="button" className="btn btn-primary px-4 rounded-pill shadow-sm" onClick={() => setShowAiInfoModal(false)}>Got it, thanks!</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertPolicyBuilder;
